import React, { useState, useEffect, useRef } from 'react';
import {
    ArrowLeft, ArrowRight, CheckCircle, Loader2,
    BookOpen, Target, Award, Info, Shield, Sparkles, Save, X,
    Zap, Layers, Clock, BarChart2
} from 'lucide-react';
import EvidenceUploader from './components/EvidenceUploader';
import QuestionRenderer, { getQuestionType } from './components/QuestionRenderer';
import DetailedQuestionPage from './components/DetailedQuestionPage';
import apiService from '../../../services/api';

// Where the user was in the quiz, remembered per audit so closing or
// reloading the page resumes there instead of at the first question.
// localStorage covers plain navigation (instant, no network); the server copy
// (`quiz_resume` on the audit, written with every saved answer) covers a
// different browser/device. The newer of the two wins.
const resumeKey = (auditId) => `sqaaf_quiz_resume_${auditId}`;
const readLocalResume = (auditId) => {
    try { return JSON.parse(localStorage.getItem(resumeKey(auditId)) || 'null'); } catch { return null; }
};
const writeLocalResume = (auditId, cursor, quizMode) => {
    try {
        localStorage.setItem(resumeKey(auditId), JSON.stringify({ ...cursor, quiz_mode: quizMode, saved_at: new Date().toISOString() }));
    } catch { /* storage full / disabled — server copy still applies */ }
};
const pickResume = (auditId, serverResume) => {
    const local = readLocalResume(auditId);
    const t = (r) => (r?.saved_at ? new Date(r.saved_at).getTime() || 0 : 0);
    const best = t(local) >= t(serverResume) ? local : serverResume;
    return best?.indicator_id ? best : null;
};

const SQAAFQuizWizard = ({ domains, initialAudit, onComplete, onExit }) => {
    const [showIntro, setShowIntro]                   = useState(true);
    const [quizMode, setQuizMode]                     = useState(null);          // null | 'short' | 'detailed'
    const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
    const [currentSubIndex, setCurrentSubIndex]       = useState(0);
    const [currentIndIndex, setCurrentIndIndex]       = useState(0);
    const [responses, setResponses]                   = useState({});
    const [isSaving, setIsSaving]                     = useState(false);
    const [justSaved, setJustSaved]                   = useState(false);
    const [autoEvidence, setAutoEvidence]             = useState(null);
    const [showProgress, setShowProgress]             = useState(false);
    const [detailedQuestions, setDetailedQuestions]   = useState({});            // indicator_id → question[]
    const [detailedResponses, setDetailedResponses]   = useState({});            // indicator_id → {question_id: value}
    const [loadingDetailed, setLoadingDetailed]       = useState(false);
    const [detailedPartial, setDetailedPartial]       = useState({});            // indicator_id → {question_id: value} for half-answered indicators

    // Last known quiz position (indicator + sub-question), see resumeKey above.
    const cursorRef       = useRef({ indicator_id: null, question_index: 0 });
    const resumeRef       = useRef(null);       // saved position loaded for this audit
    const resumeApplied   = useRef(false);      // resume is applied once, on entry
    const saveChain       = useRef(Promise.resolve()); // saves run one at a time, in order

    const findIndicatorPos = (indicatorId) => {
        for (let d = 0; d < (domains || []).length; d++)
            for (let s = 0; s < (domains[d].subdomains || []).length; s++)
                for (let i = 0; i < (domains[d].subdomains[s].indicators || []).length; i++)
                    if (domains[d].subdomains[s].indicators[i].indicator_id === indicatorId) return { d, s, i };
        return null;
    };

    // Saves are chained so a slow earlier request can never land after (and
    // overwrite) a later one — important with 20-30 answers per indicator.
    const enqueueSave = (fn) => {
        const run = saveChain.current.then(fn, fn);
        saveChain.current = run.catch(() => {});
        return run;
    };
    // Restore previous responses + resume position
    useEffect(() => {
        if (!initialAudit?.responses || !domains?.length) return;
        const map = {};
        const detailedMap = {};
        const partialMap = {};
        initialAudit.responses.forEach(r => {
            map[r.indicator_id] = r;
            // Restore detailed sub-answers from a previous detailed session
            if (r.quiz_mode === 'detailed' && r.detailed_responses?.length) {
                const subAnswers = {};
                r.detailed_responses.forEach(dr => { subAnswers[dr.question_id] = dr.selected_value; });
                // Fully answered indicators have a level; half-answered ones don't yet.
                if (r.selected_level && !r.is_partial) detailedMap[r.indicator_id] = subAnswers;
                else partialMap[r.indicator_id] = subAnswers;
            }
        });
        setResponses(map);
        setDetailedResponses(detailedMap);
        setDetailedPartial(partialMap);
        // Find first short-mode unanswered position (used as resume point for short mode)
        let found = false;
        for (let dIdx = 0; dIdx < domains.length && !found; dIdx++)
            for (let sIdx = 0; sIdx < (domains[dIdx].subdomains || []).length && !found; sIdx++)
                for (let iIdx = 0; iIdx < (domains[dIdx].subdomains[sIdx].indicators || []).length && !found; iIdx++)
                    if (!map[domains[dIdx].subdomains[sIdx].indicators[iIdx].indicator_id]?.selected_level) {
                        setCurrentDomainIndex(dIdx); setCurrentSubIndex(sIdx); setCurrentIndIndex(iIdx); found = true;
                    }

        // Resume: jump to where the user last was (indicator + sub-question).
        // The intro screen is still shown (mode pre-selected, "Continue" button)
        // so they can switch assessment type, but nothing restarts from Q1.
        const resume = initialAudit?._id ? pickResume(initialAudit._id, initialAudit.quiz_resume) : null;
        const pos = resume ? findIndicatorPos(resume.indicator_id) : null;
        if (resume && pos) {
            resumeRef.current = resume;
            cursorRef.current = { indicator_id: resume.indicator_id, question_index: resume.question_index || 0 };
            setCurrentDomainIndex(pos.d); setCurrentSubIndex(pos.s); setCurrentIndIndex(pos.i);
            if (resume.quiz_mode === 'short' || resume.quiz_mode === 'detailed') setQuizMode(resume.quiz_mode);
        }
    }, [initialAudit, domains]); // eslint-disable-line react-hooks/exhaustive-deps

    // Remember the position whenever the user moves to another indicator.
    useEffect(() => {
        if (showIntro || !quizMode || !initialAudit?._id) return;
        const id = domains?.[currentDomainIndex]?.subdomains?.[currentSubIndex]?.indicators?.[currentIndIndex]?.indicator_id;
        if (!id) return;
        if (cursorRef.current.indicator_id !== id) cursorRef.current = { indicator_id: id, question_index: 0 };
        writeLocalResume(initialAudit._id, cursorRef.current, quizMode);
    }, [showIntro, quizMode, currentDomainIndex, currentSubIndex, currentIndIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-evidence hints
    useEffect(() => {
        apiService.sqaaf.getAutoEvidence()
            .then(data => { if (data?.hints) { const m = {}; data.hints.forEach(h => { m[h.indicator_id] = h; }); setAutoEvidence({ hints: m }); } })
            .catch(() => {});
    }, []);

    // Fetch all detailed questions when detailed mode selected; also reset resume position
    useEffect(() => {
        if (quizMode !== 'detailed') return;
        setLoadingDetailed(true);
        apiService.sqaaf.getDetailedQuestions()
            .then(data => {
                if (data?.questions) setDetailedQuestions(data.questions);
            })
            .catch(() => {})
            .finally(() => setLoadingDetailed(false));

        // First entry with a saved position: stay where the user left off.
        if (!domains?.length) return;
        if (!resumeApplied.current && resumeRef.current?.quiz_mode === 'detailed') {
            resumeApplied.current = true;
            return;
        }
        resumeApplied.current = true;

        // Otherwise: first indicator not yet answered in detailed mode
        let found = false;
        for (let dIdx = 0; dIdx < domains.length && !found; dIdx++)
            for (let sIdx = 0; sIdx < (domains[dIdx].subdomains || []).length && !found; sIdx++)
                for (let iIdx = 0; iIdx < (domains[dIdx].subdomains[sIdx].indicators || []).length && !found; iIdx++) {
                    const id = domains[dIdx].subdomains[sIdx].indicators[iIdx].indicator_id;
                    const doneDetailed = !!(detailedResponses[id] ||
                        (responses[id]?.quiz_mode === 'detailed' && responses[id]?.selected_level));
                    if (!doneDetailed) {
                        setCurrentDomainIndex(dIdx); setCurrentSubIndex(sIdx); setCurrentIndIndex(iIdx); found = true;
                    }
                }
        if (!found) { setCurrentDomainIndex(0); setCurrentSubIndex(0); setCurrentIndIndex(0); }
    }, [quizMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const flashSaved = () => { setJustSaved(true); setTimeout(() => setJustSaved(false), 1800); };

    if (!domains?.length) return (
        <div className="flex items-center justify-center min-h-[60vh] font-[Poppins]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D67C2C]" />
            <span className="ml-3 text-gray-500">Loading…</span>
        </div>
    );

    // mode-aware: detailed counts only indicators answered via sub-questions
    const isIndicatorAnswered = (indicatorId, mode) => {
        if (mode === 'detailed') {
            // Count answered if sub-questions completed in this session OR previous detailed session
            return !!(detailedResponses[indicatorId] ||
                (responses[indicatorId]?.quiz_mode === 'detailed' && responses[indicatorId]?.selected_level));
        }
        return !!responses[indicatorId]?.selected_level;
    };

    const getOverallStats = () => {
        const mode = quizMode || 'short';
        let total = 0, answered = 0;
        domains.forEach(d => (d.subdomains || []).forEach(s => (s.indicators || []).forEach(i => {
            total++;
            if (isIndicatorAnswered(i.indicator_id, mode)) answered++;
        })));
        return { total, answered, percent: total > 0 ? Math.round((answered / total) * 100) : 0 };
    };

    const getDomainProgress = (domain) => {
        const mode = quizMode || 'short';
        const indicators = (domain.subdomains || []).flatMap(s => s.indicators || []);
        const answered = indicators.filter(i => isIndicatorAnswered(i.indicator_id, mode)).length;
        return { answered, total: indicators.length, percent: indicators.length > 0 ? Math.round((answered / indicators.length) * 100) : 0 };
    };

    const stats = getOverallStats();

    // ── INTRO — mode selection then domain list ─────────────────────────────
    if (showIntro) {
        // Step 1: pick mode
        if (!quizMode) {
            return (
                <div className="font-[Poppins] min-h-screen bg-gray-50 px-4 py-10">
                    <div className="max-w-xl mx-auto">
                        <button onClick={onExit} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#D67C2C] transition-colors mb-8 cursor-pointer group">
                            <span className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center group-hover:border-[#D67C2C]/30 transition-colors">
                                <ArrowLeft className="w-3.5 h-3.5" />
                            </span>
                            Back to Dashboard
                        </button>

                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-5">
                            <Award className="w-3.5 h-3.5 text-[#D67C2C]" />
                            <span className="text-xs font-semibold text-[#D67C2C]">SQAAF Self-Assessment</span>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Choose your assessment type</h1>
                        <p className="text-gray-500 text-sm leading-relaxed mb-8">
                            Both types produce the same report, scores, and action plan. Choose based on how much time you have and how precise you need the results.
                        </p>

                        <div className="space-y-4 mb-6">
                            {/* Short mode card */}
                            <button
                                onClick={() => setQuizMode('short')}
                                className="w-full text-left bg-white rounded-2xl border-2 border-gray-200 hover:border-[#D67C2C] shadow-sm hover:shadow-md p-6 transition-all group cursor-pointer"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center flex-shrink-0 transition-colors">
                                        <Zap className="w-5 h-5 text-[#D67C2C]" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-gray-900">Quick Assessment</h3>
                                            <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">Recommended to start</span>
                                        </div>
                                        <p className="text-sm text-gray-500 leading-relaxed mb-4">One rubric selection per indicator. Best for schools doing SQAAF for the first time or when you're short on time.</p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { icon: Target, text: '84 questions' },
                                                { icon: Clock, text: '~45 min' },
                                                { icon: BarChart2, text: 'Standard accuracy' },
                                            ].map(({ icon: Icon, text }) => (
                                                <div key={text} className="flex items-center gap-1.5 text-xs text-gray-500">
                                                    <Icon className="w-3.5 h-3.5 text-gray-400" />{text}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#D67C2C] flex-shrink-0 mt-1 transition-colors" />
                                </div>
                            </button>

                            {/* Detailed mode card */}
                            <button
                                onClick={() => setQuizMode('detailed')}
                                className="w-full text-left bg-white rounded-2xl border-2 border-gray-200 hover:border-[#1D3469] shadow-sm hover:shadow-md p-6 transition-all group cursor-pointer"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center flex-shrink-0 transition-colors">
                                        <Layers className="w-5 h-5 text-[#1D3469]" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-gray-900">Comprehensive Assessment</h3>
                                            <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">Most accurate</span>
                                        </div>
                                        <p className="text-sm text-gray-500 leading-relaxed mb-4">20–30 targeted sub-questions per indicator. Reveals gaps the quick assessment might miss. Ideal for detailed accreditation preparation.</p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { icon: Target, text: '~2000+ questions' },
                                                { icon: Clock, text: '3–4 hours' },
                                                { icon: BarChart2, text: 'High accuracy' },
                                            ].map(({ icon: Icon, text }) => (
                                                <div key={text} className="flex items-center gap-1.5 text-xs text-gray-500">
                                                    <Icon className="w-3.5 h-3.5 text-gray-400" />{text}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#1D3469] flex-shrink-0 mt-1 transition-colors" />
                                </div>
                            </button>
                        </div>

                        {stats.answered > 0 && (
                            <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-100 rounded-xl text-sm text-[#D67C2C]">
                                <Info className="w-4 h-4 flex-shrink-0" />
                                <span>{stats.answered} of {stats.total} already answered — we'll resume where you left off.</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // Step 2: mode selected — show domain overview + start
        return (
            <div className="font-[Poppins] min-h-screen bg-gray-50 px-4 py-10">
                <div className="max-w-xl mx-auto">
                    <button onClick={() => setQuizMode(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#D67C2C] transition-colors mb-8 cursor-pointer group">
                        <span className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center group-hover:border-[#D67C2C]/30 transition-colors">
                            <ArrowLeft className="w-3.5 h-3.5" />
                        </span>
                        Change assessment type
                    </button>

                    <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-8 mb-5">
                        <div className="flex items-center gap-2 mb-5">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${quizMode === 'detailed' ? 'bg-indigo-50' : 'bg-orange-50'}`}>
                                {quizMode === 'detailed' ? <Layers className="w-4 h-4 text-[#1D3469]" /> : <Zap className="w-4 h-4 text-[#D67C2C]" />}
                            </div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100">
                                <Award className="w-3.5 h-3.5 text-[#D67C2C]" />
                                <span className="text-xs font-semibold text-[#D67C2C]">
                                    {quizMode === 'detailed' ? 'Comprehensive Assessment' : 'Quick Assessment'}
                                </span>
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">How is your school doing?</h1>
                        <p className="text-gray-500 text-sm leading-relaxed mb-6">
                            {quizMode === 'detailed'
                                ? `Answer 20–30 detailed questions per indicator across ${domains.length} key areas. Progress is saved automatically.`
                                : `Answer questions about your school's practices across ${domains.length} key areas. Takes about 45 minutes — progress is saved automatically.`
                            }
                        </p>

                        <div className="grid grid-cols-3 gap-3 pt-5 border-t border-gray-100">
                            {[
                                { icon: Target,   label: quizMode === 'detailed' ? '~2000 Questions' : `${stats.total} Questions` },
                                { icon: Shield,   label: 'Auto-Saved' },
                                { icon: Sparkles, label: 'Instant Report' },
                            ].map(({ icon: Icon, label }) => (
                                <div key={label} className="text-center p-3 rounded-xl bg-gray-50">
                                    <Icon className="w-4 h-4 text-[#D67C2C] mx-auto mb-1.5" />
                                    <p className="text-xs font-semibold text-gray-700">{label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-5">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Areas you'll cover</p>
                        <div className="space-y-1.5">
                            {domains.map((d, idx) => {
                                const { answered, total } = getDomainProgress(d);
                                const done = answered === total && total > 0;
                                return (
                                    <div key={d.domain_id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <span className="w-5 h-5 rounded-md bg-orange-100 text-[#D67C2C] flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                                        <p className="flex-1 text-sm text-gray-700 truncate">{d.domain_name}</p>
                                        {done
                                            ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                            : <span className="text-xs text-gray-400 flex-shrink-0">{total}q</span>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {(stats.answered > 0 || resumeRef.current) && (
                        <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-100 rounded-xl mb-5 text-sm text-[#D67C2C]">
                            <Info className="w-4 h-4 flex-shrink-0" />
                            <span>
                                {stats.answered} of {stats.total} answered — we'll resume where you left off
                                {resumeRef.current && resumeRef.current.quiz_mode === quizMode
                                    ? ` (indicator ${resumeRef.current.indicator_id}${quizMode === 'detailed' ? `, question ${(cursorRef.current.question_index || 0) + 1}` : ''}).`
                                    : '.'}
                            </span>
                        </div>
                    )}

                    {loadingDetailed ? (
                        <div className="w-full py-3.5 bg-gray-100 rounded-xl flex items-center justify-center gap-2 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading questions…
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowIntro(false)}
                            className="w-full py-3.5 bg-[#D67C2C] hover:bg-[#c06a25] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-md cursor-pointer"
                        >
                            {(stats.answered > 0 || resumeRef.current) ? 'Continue Assessment' : 'Start Assessment'}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ── QUIZ ──────────────────────────────────────────────────────────────────
    const currentDomain    = domains[currentDomainIndex];
    const currentSubdomain = currentDomain?.subdomains?.[currentSubIndex];
    const currentIndicator = currentSubdomain?.indicators?.[currentIndIndex];
    const totalDomains     = domains.length;
    const activeMode       = quizMode || 'short';

    if (!currentDomain || !currentSubdomain || !currentIndicator)
        return <div className="p-8 text-gray-400 font-[Poppins]">No content found.</div>;

    const getCurrentPosition = () => {
        let pos = 0;
        for (let d = 0; d < currentDomainIndex; d++)
            (domains[d].subdomains || []).forEach(s => { pos += (s.indicators || []).length; });
        for (let s = 0; s < currentSubIndex; s++)
            pos += (currentDomain.subdomains[s].indicators || []).length;
        return pos + currentIndIndex + 1;
    };

    const currentPosition = getCurrentPosition();
    const currentResponse = responses[currentIndicator.indicator_id] || {};
    const selectedLevel   = currentResponse.selected_level;
    const hasEvidence     = currentResponse.evidence_files?.length > 0;
    const isLastQuestion  = currentDomainIndex === totalDomains - 1
        && currentSubIndex === currentDomain.subdomains.length - 1
        && currentIndIndex === currentSubdomain.indicators.length - 1;
    const isFirstQuestion = currentDomainIndex === 0 && currentSubIndex === 0 && currentIndIndex === 0;

    // Detailed questions for current indicator
    const currentDetailedQs = (activeMode === 'detailed') ? (detailedQuestions[currentIndicator.indicator_id] || []) : [];
    const useDetailed = activeMode === 'detailed' && currentDetailedQs.length > 0;

    // Mode-aware: can user proceed to next indicator?
    const canProceed = useDetailed
        ? !!detailedResponses[currentIndicator.indicator_id]   // sub-questions completed
        : !!selectedLevel;                                      // rubric level selected

    // Short mode: direct level selection
    const handleLevelSelect = async (level) => {
        const indicatorId = currentIndicator.indicator_id;
        const newResponse = { ...responses[indicatorId], indicator_id: indicatorId, selected_level: level, saved_at: new Date().toISOString() };
        setResponses(prev => ({ ...prev, [indicatorId]: newResponse }));
        try {
            setIsSaving(true);
            await enqueueSave(() => apiService.sqaaf.saveResponse(
                initialAudit._id, indicatorId, level,
                newResponse.evidence_files, newResponse.notes,
                'short', null, { resume: { question_index: 0 } }
            ));
            flashSaved();
        } catch (err) { console.error('Auto-save failed', err); }
        finally { setIsSaving(false); }
    };

    // Detailed mode: callback from DetailedQuestionPage when all sub-questions answered
    const handleDetailedComplete = async (indicatorId, subResponses, computedLevel) => {
        // subResponses: [{question_id, selected_value}, ...]
        const newResponse = { ...responses[indicatorId], indicator_id: indicatorId, selected_level: computedLevel, quiz_mode: 'detailed', saved_at: new Date().toISOString() };
        setResponses(prev => ({ ...prev, [indicatorId]: newResponse }));
        // Store as map {question_id: value} for isIndicatorAnswered check
        const subMap = {};
        subResponses.forEach(r => { subMap[r.question_id] = r.selected_value; });
        setDetailedResponses(prev => ({ ...prev, [indicatorId]: subMap }));
        setDetailedPartial(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== indicatorId)));
        try {
            setIsSaving(true);
            await enqueueSave(() => apiService.sqaaf.saveResponse(
                initialAudit._id, indicatorId, computedLevel,
                newResponse.evidence_files, newResponse.notes,
                'detailed', subResponses, { resume: { question_index: cursorRef.current.question_index || 0 } }
            ));
            flashSaved();
        } catch (err) { console.error('Auto-save failed', err); }
        finally { setIsSaving(false); }
    };

    // Detailed mode, indicator only partly answered: save the answers so far
    // (without scoring the indicator) so closing the page loses nothing.
    const handleDetailedProgress = async (indicatorId, partialResponses, nextQuestionIndex) => {
        const subMap = {};
        partialResponses.forEach(r => { subMap[r.question_id] = r.selected_value; });
        cursorRef.current = { indicator_id: indicatorId, question_index: nextQuestionIndex };
        writeLocalResume(initialAudit._id, cursorRef.current, 'detailed');
        // An indicator that was already complete stays complete while it is edited.
        if (detailedResponses[indicatorId]) setDetailedResponses(prev => ({ ...prev, [indicatorId]: subMap }));
        else setDetailedPartial(prev => ({ ...prev, [indicatorId]: subMap }));
        try {
            setIsSaving(true);
            await enqueueSave(() => apiService.sqaaf.saveResponse(
                initialAudit._id, indicatorId, null,
                responses[indicatorId]?.evidence_files, responses[indicatorId]?.notes,
                'detailed', partialResponses, { partial: true, resume: { question_index: nextQuestionIndex } }
            ));
            flashSaved();
        } catch (err) { console.error('Auto-save failed', err); }
        finally { setIsSaving(false); }
    };

    const handleDetailedPosition = (indicatorId, questionIndex) => {
        cursorRef.current = { indicator_id: indicatorId, question_index: questionIndex };
        if (initialAudit?._id) writeLocalResume(initialAudit._id, cursorRef.current, 'detailed');
    };

    const handleNext = () => {
        if (currentIndIndex < currentSubdomain.indicators.length - 1) { setCurrentIndIndex(p => p + 1); }
        else if (currentSubIndex < currentDomain.subdomains.length - 1) { setCurrentSubIndex(p => p + 1); setCurrentIndIndex(0); }
        else if (currentDomainIndex < totalDomains - 1) { setCurrentDomainIndex(p => p + 1); setCurrentSubIndex(0); setCurrentIndIndex(0); }
        else { onComplete?.(); }
    };

    const handlePrev = () => {
        if (currentIndIndex > 0) { setCurrentIndIndex(p => p - 1); }
        else if (currentSubIndex > 0) { setCurrentSubIndex(p => p - 1); setCurrentIndIndex(currentDomain.subdomains[currentSubIndex - 1].indicators.length - 1); }
        else if (currentDomainIndex > 0) {
            const prevDom = domains[currentDomainIndex - 1];
            setCurrentDomainIndex(p => p - 1);
            setCurrentSubIndex(prevDom.subdomains.length - 1);
            setCurrentIndIndex(prevDom.subdomains[prevDom.subdomains.length - 1].indicators.length - 1);
        }
    };

    const jumpToDomain = (idx) => { setCurrentDomainIndex(idx); setCurrentSubIndex(0); setCurrentIndIndex(0); setShowProgress(false); };

    const refreshAudit = async () => {
        const audit = await apiService.sqaaf.getCurrentAudit(initialAudit.school_id);
        if (audit) { const map = {}; audit.responses.forEach(r => { map[r.indicator_id] = r; }); setResponses(map); }
    };

    return (
        <div className="font-[Poppins] min-h-screen bg-gray-50">

            {/* ── Top bar ─────────────────────────────── */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center gap-4 py-3">
                        <button onClick={onExit} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#D67C2C] transition-colors group cursor-pointer flex-shrink-0">
                            <span className="w-6 h-6 rounded-md bg-gray-100 group-hover:bg-orange-50 flex items-center justify-center transition-colors">
                                <ArrowLeft className="w-3 h-3" />
                            </span>
                            <span className="hidden sm:block">Dashboard</span>
                        </button>

                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-400">{currentDomain.domain_name}</span>
                                    {activeMode === 'detailed' && (
                                        <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">Detailed</span>
                                    )}
                                </div>
                                <span className="text-xs font-semibold text-[#D67C2C]">{currentPosition} / {stats.total}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div className="h-full rounded-full bg-[#D67C2C] transition-all duration-500" style={{ width: `${stats.percent}%` }} />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                            {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                            {!isSaving && justSaved && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                            <button
                                onClick={() => setShowProgress(p => !p)}
                                className="text-xs font-medium text-gray-500 hover:text-[#D67C2C] transition-colors cursor-pointer border border-gray-200 hover:border-[#D67C2C]/30 px-2.5 py-1 rounded-lg"
                            >
                                {stats.percent}%
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Progress drawer ─────────────────────────── */}
            {showProgress && (
                <div className="bg-white border-b border-gray-100 shadow-sm">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Domain Progress</p>
                            <button onClick={() => setShowProgress(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {domains.map((d, idx) => {
                                const { answered, total, percent } = getDomainProgress(d);
                                const isCurrent = idx === currentDomainIndex;
                                const isDone = answered === total && total > 0;
                                return (
                                    <button
                                        key={d.domain_id}
                                        onClick={() => jumpToDomain(idx)}
                                        className={`flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors cursor-pointer ${isCurrent ? 'bg-orange-50 border border-orange-100' : 'hover:bg-gray-50'}`}
                                    >
                                        <span className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center text-xs font-bold ${isDone ? 'bg-green-500 text-white' : isCurrent ? 'bg-[#D67C2C] text-white' : 'bg-gray-100 text-gray-500'}`}>
                                            {isDone ? '✓' : idx + 1}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-700 truncate">{d.domain_name}</p>
                                            <div className="w-full bg-gray-100 rounded-full h-1 mt-1">
                                                <div className={`h-full rounded-full ${isDone ? 'bg-green-500' : 'bg-[#D67C2C]'}`} style={{ width: `${percent}%` }} />
                                            </div>
                                        </div>
                                        <span className="text-xs text-gray-400 flex-shrink-0">{answered}/{total}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Main content ─────────────────────────────────────────── */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">

                <p className="text-xs text-gray-400 mb-4">
                    <span className="font-medium text-gray-500">{currentDomain.domain_name}</span>
                    <span className="mx-2">›</span>
                    {currentSubdomain.subdomain_name}
                </p>

                {/* Detailed mode: show multi-question page */}
                {useDetailed ? (
                    <DetailedQuestionPage
                        key={currentIndicator.indicator_id}
                        indicator={currentIndicator}
                        questions={currentDetailedQs}
                        savedResponses={detailedResponses[currentIndicator.indicator_id] || detailedPartial[currentIndicator.indicator_id] || null}
                        initialIndex={cursorRef.current.indicator_id === currentIndicator.indicator_id ? cursorRef.current.question_index : undefined}
                        onComplete={handleDetailedComplete}
                        onProgress={handleDetailedProgress}
                        onPositionChange={handleDetailedPosition}
                        isSaving={isSaving}
                        justSaved={justSaved}
                    />
                ) : (
                    /* Short mode: single question card */
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 mb-5">
                        <span className="inline-block text-xs font-bold text-[#D67C2C] bg-orange-50 px-2.5 py-1 rounded-md mb-4">
                            {currentIndicator.indicator_id}
                        </span>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-800 leading-relaxed mb-8">
                            {currentIndicator.statement}
                        </h2>
                        <QuestionRenderer
                            indicator={currentIndicator}
                            selectedLevel={selectedLevel}
                            onSelect={handleLevelSelect}
                        />
                    </div>
                )}

                {/* Evidence — only in short mode or when already has evidence */}
                {!useDetailed && (selectedLevel >= 3 || hasEvidence || autoEvidence?.hints?.[currentIndicator.indicator_id]) && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-5">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-[#D67C2C]" />
                            Supporting Evidence
                            {selectedLevel >= 3 && !hasEvidence && (
                                <span className="ml-auto text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Recommended</span>
                            )}
                        </h4>
                        {autoEvidence?.hints?.[currentIndicator.indicator_id] && (
                            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                                💡 {autoEvidence.hints[currentIndicator.indicator_id].hint}
                            </p>
                        )}
                        <EvidenceUploader
                            indicatorId={currentIndicator.indicator_id}
                            auditId={initialAudit._id}
                            currentFiles={currentResponse.evidence_files}
                            onUploadComplete={refreshAudit}
                            onDeleteComplete={refreshAudit}
                        />
                    </div>
                )}

                {/* Navigation */}
                <div className="flex gap-3 mb-3">
                    <button
                        onClick={handlePrev}
                        disabled={isFirstQuestion}
                        className="flex items-center gap-1.5 px-5 py-3 rounded-xl border border-gray-200 text-sm text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button
                        onClick={handleNext}
                        disabled={!canProceed}
                        className="flex-1 py-3 rounded-xl bg-[#D67C2C] hover:bg-[#c06a25] text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                        {isLastQuestion ? 'Finish Assessment' : 'Next Question'}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {!canProceed && (
                    <p className="text-center text-xs text-gray-400 mb-3">
                        {useDetailed ? 'Answer all sub-questions to continue' : 'Select an answer to continue'}
                    </p>
                )}

                <button
                    onClick={onExit}
                    className="w-full flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 py-2 transition-colors cursor-pointer"
                >
                    <Save className="w-3 h-3" /> Save &amp; Exit — continue later
                </button>
            </div>
        </div>
    );
};

export default SQAAFQuizWizard;
